import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  Pagination,
  Alert,
} from '../../components/ui';
import {
  getPeriodeList,
  createPeriode,
  updatePeriode,
  deletePeriode,
  lockPeriode,
  unlockPeriode,
} from '../../features/absensi/api';
import { RoleCode, type Periode } from '@app/shared';
import { useAuth } from '../../features/auth/use-auth';

const BULAN = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

const STATUS_MAP: Record<string, { label: string; color: 'green' | 'gray' | 'blue' }> = {
  draft: { label: 'Draft', color: 'gray' },
  aktif: { label: 'Aktif', color: 'green' },
  ditutup: { label: 'Ditutup', color: 'blue' },
};

type FormState = {
  id?: number;
  nama: string;
  kode_periode: string;
  bulan: number;
  tahun: number;
  tanggal_mulai: string;
  tanggal_selesai: string;
  status: 'draft' | 'aktif' | 'ditutup';
};

type LockAction = 'lock' | 'unlock' | null;

export function PeriodePage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<Periode[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    nama: '',
    kode_periode: '',
    bulan: new Date().getMonth() + 1,
    tahun: new Date().getFullYear(),
    tanggal_mulai: '',
    tanggal_selesai: '',
    status: 'draft',
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Lock/unlock reason modal state
  const [lockAction, setLockAction] = useState<LockAction>(null);
  const [lockTarget, setLockTarget] = useState<Periode | null>(null);
  const [lockReason, setLockReason] = useState('');
  const [lockLoading, setLockLoading] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  const load = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPeriodeList({
        page,
        limit: 10,
        search: search || undefined,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search]);

  const openCreate = () => {
    setForm({
      nama: '',
      kode_periode: '',
      bulan: new Date().getMonth() + 1,
      tahun: new Date().getFullYear(),
      tanggal_mulai: '',
      tanggal_selesai: '',
      status: 'draft',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: Periode) => {
    setForm({
      id: row.id,
      nama: row.nama,
      kode_periode: row.kode_periode,
      bulan: row.bulan,
      tahun: row.tahun,
      tanggal_mulai: row.tanggal_mulai,
      tanggal_selesai: row.tanggal_selesai,
      status: row.status,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.nama.trim() || !form.kode_periode.trim()) {
      setFormError('Nama dan kode periode wajib diisi');
      return;
    }
    if (!form.tanggal_mulai || !form.tanggal_selesai) {
      setFormError('Tanggal mulai dan selesai wajib diisi');
      return;
    }
    try {
      const body = {
        nama: form.nama.trim(),
        kode_periode: form.kode_periode.trim(),
        bulan: form.bulan,
        tahun: form.tahun,
        tanggal_mulai: form.tanggal_mulai,
        tanggal_selesai: form.tanggal_selesai,
        status: form.status,
      };
      if (form.id) {
        await updatePeriode(form.id, {
          nama: body.nama,
          tanggal_mulai: body.tanggal_mulai,
          tanggal_selesai: body.tanggal_selesai,
        });
        setSuccess('Periode berhasil diperbarui');
      } else {
        await createPeriode({
          nama: body.nama,
          kode_periode: body.kode_periode,
          bulan: body.bulan,
          tahun: body.tahun,
          tanggal_mulai: body.tanggal_mulai,
          tanggal_selesai: body.tanggal_selesai,
          status: body.status,
        });
        setSuccess('Periode berhasil dibuat');
      }
      setModalOpen(false);
      load(meta.page);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Yakin ingin menghapus periode ini?')) return;
    try {
      await deletePeriode(id);
      setSuccess('Periode berhasil dihapus');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus');
    }
  };

  const openLockModal = (row: Periode, action: 'lock' | 'unlock') => {
    setLockTarget(row);
    setLockAction(action);
    setLockReason('');
    setLockError(null);
  };

  const closeLockModal = () => {
    setLockTarget(null);
    setLockAction(null);
    setLockReason('');
    setLockError(null);
    setLockLoading(false);
  };

  const handleLockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lockTarget || !lockAction) return;
    const reason = lockReason.trim();
    if (reason.length < 3) {
      setLockError('Alasan minimal 3 karakter');
      return;
    }
    if (reason.length > 500) {
      setLockError('Alasan maksimal 500 karakter');
      return;
    }
    setLockLoading(true);
    setLockError(null);
    try {
      if (lockAction === 'lock') {
        await lockPeriode(lockTarget.id, reason);
        setSuccess(`Periode ${lockTarget.kode_periode} berhasil dikunci`);
      } else {
        await unlockPeriode(lockTarget.id, reason);
        setSuccess(`Periode ${lockTarget.kode_periode} berhasil dibuka kembali`);
      }
      closeLockModal();
      load(meta.page);
    } catch (e) {
      setLockError(e instanceof Error ? e.message : 'Gagal memproses aksi');
    } finally {
      setLockLoading(false);
    }
  };

  const fmtDate = (d: string) => {
    if (!d) return '-';
    const parts = d.split('-');
    if (parts.length !== 3) return d;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  return (
    <AdminLayout title="Periode Absensi">
      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <Card>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Input
            placeholder="Cari periode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          {canManage && <Button onClick={openCreate}>Tambah Periode</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Kode</Th>
              <Th>Nama</Th>
              <Th>Bulan</Th>
              <Th>Tahun</Th>
              <Th>Tgl Mulai</Th>
              <Th>Tgl Selesai</Th>
              <Th>Status</Th>
              {canManage && <Th style={{ textAlign: 'right' }}>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>{row.kode_periode}</Td>
                <Td>{row.nama}</Td>
                <Td>{BULAN[row.bulan - 1] ?? row.bulan}</Td>
                <Td>{row.tahun}</Td>
                <Td>{fmtDate(row.tanggal_mulai)}</Td>
                <Td>{fmtDate(row.tanggal_selesai)}</Td>
                <Td>
                  <Badge color={STATUS_MAP[row.status]?.color ?? 'gray'}>
                    {STATUS_MAP[row.status]?.label ?? row.status}
                  </Badge>
                </Td>
                {canManage && (
                  <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button variant="secondary" onClick={() => openEdit(row)}>
                      Ubah
                    </Button>{' '}
                    {row.status !== 'ditutup' ? (
                      <Button variant="danger" onClick={() => openLockModal(row, 'lock')}>
                        Kunci
                      </Button>
                    ) : (
                      <Button variant="primary" onClick={() => openLockModal(row, 'unlock')}>
                        Buka Kunci
                      </Button>
                    )}{' '}
                    <Button variant="danger" onClick={() => handleDelete(row.id)}>
                      Hapus
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 8 : 7} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
        <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
      </Card>

      <Modal
        open={modalOpen && canManage}
        onClose={() => setModalOpen(false)}
        title={form.id ? 'Ubah Periode' : 'Tambah Periode'}
      >
        <form onSubmit={handleSubmit}>
          {formError && <Alert type="error">{formError}</Alert>}
          <Input
            label="Kode Periode"
            value={form.kode_periode}
            onChange={(e) => setForm({ ...form, kode_periode: e.target.value })}
            disabled={!!form.id}
            required
          />
          <Input
            label="Nama Periode"
            value={form.nama}
            onChange={(e) => setForm({ ...form, nama: e.target.value })}
            required
          />
          <Select
            label="Bulan"
            value={String(form.bulan)}
            onChange={(e) => setForm({ ...form, bulan: Number(e.target.value) })}
          >
            {BULAN.map((b, i) => (
              <option key={i} value={i + 1}>
                {b}
              </option>
            ))}
          </Select>
          <Input
            label="Tahun"
            type="number"
            value={String(form.tahun)}
            onChange={(e) => setForm({ ...form, tahun: Number(e.target.value) })}
          />
          <Input
            label="Tanggal Mulai"
            type="date"
            value={form.tanggal_mulai}
            onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })}
            required
          />
          <Input
            label="Tanggal Selesai"
            type="date"
            value={form.tanggal_selesai}
            onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })}
            required
          />
          {!form.id && (
            <Select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as 'draft' | 'aktif' | 'ditutup' })
              }
            >
              <option value="draft">Draft</option>
              <option value="aktif">Aktif</option>
              <option value="ditutup">Ditutup</option>
            </Select>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button variant="secondary" onClick={() => setModalOpen(false)} type="button">
              Batal
            </Button>
            <Button type="submit">Simpan</Button>
          </div>
        </form>
      </Modal>

      {/* Lock/Unlock reason modal */}
      <Modal
        open={!!lockTarget && canManage}
        onClose={closeLockModal}
        title={lockAction === 'lock' ? 'Kunci Periode' : 'Buka Kunci Periode'}
      >
        <form onSubmit={handleLockSubmit}>
          {lockTarget && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {lockTarget.kode_periode} — {lockTarget.nama}
              </p>
              <p style={{ margin: '0.25rem 0 0', color: '#64748B', fontSize: '0.875rem' }}>
                Status saat ini: {STATUS_MAP[lockTarget.status]?.label ?? lockTarget.status}
              </p>
            </div>
          )}
          {lockError && <Alert type="error">{lockError}</Alert>}
          <Input
            label="Alasan"
            value={lockReason}
            onChange={(e) => setLockReason(e.target.value)}
            placeholder="Jelaskan alasan kunci/buka kunci periode (min. 3 karakter)"
            required
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button
              variant="secondary"
              onClick={closeLockModal}
              type="button"
              disabled={lockLoading}
            >
              Batal
            </Button>
            <Button type="submit" disabled={lockLoading}>
              {lockLoading
                ? 'Memproses...'
                : lockAction === 'lock'
                  ? 'Kunci Periode'
                  : 'Buka Kunci Periode'}
            </Button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
}
